import { Box, BoxProps } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'

export interface LatestFeedbackLayoutBoxProps extends BoxProps {
  /**
   * Container content
   */
  children: React.ReactNode
  /**
   * Container variant - affects background and styling
   * - team: Gradient background (default)
   * - individual: Heritage blue background
   * - achievements: Heritage blue background (same as individual, for future customization)
   */
  variant?: 'team' | 'individual' | 'achievements'
  /**
   * Design variant affects box styling
   * - standard: Uses standard box styling matching EmotionStateSection (gradient background, white08 border, 16px radius, blur)
   * - legacy: Keeps current styling (heritageBlue40 background, white04 border, 10px radius, no blur)
   */
  designVariant?: 'standard' | 'legacy'
}

export const LatestFeedbackLayoutBox = ({
  children,
  variant,
  designVariant = 'legacy',
  sx,
  ref,
  ...props
}: LatestFeedbackLayoutBoxProps) => {
  // Standard box styling matching EmotionStateSection
  const standardBoxStyles = {
    background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
    border: `1px solid ${INTERAX_COLORS.white08}`,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(6px)',
    borderRadius: '16px',
    padding: { xs: '20px', sm: '20px', md: '30px' },
    alignItems: 'stretch',
    gap: { xs: '16px', sm: '16px', md: '24px' }
  }

  // Legacy box styling
  const legacyBackground =
    variant === 'individual' || variant === 'achievements'
      ? INTERAX_COLORS.heritageBlue40
      : 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.10) 100%)'

  const legacyBoxStyles = {
    background: legacyBackground,
    border: `1px solid ${INTERAX_COLORS.white04}`,
    boxShadow: `inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
    borderRadius: '10px',
    padding: '0'
  }

  return (
    <Box
      ref={ref}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: designVariant === 'standard' ? 'stretch' : 'center',
        justifyContent: 'flex-start',
        gap: designVariant === 'standard' ? undefined : '24px',
        minWidth: 0, // Prevents flex item from overflowing
        ...(designVariant === 'standard' ? standardBoxStyles : legacyBoxStyles),
        // Custom styles
        ...sx
      }}
      {...props}
    >
      {children}
    </Box>
  )
}

export default LatestFeedbackLayoutBox
